import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { Alert, Button, Drawer, Empty, Segmented } from 'antd'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { BillActionDialog, type BillOperation } from './bill-action-dialog.tsx'
import { BillDetail } from './bill-detail.tsx'
import { BillEditor } from './bill-editor.tsx'
import { createBillNoteColumn, createBillSummaryColumns } from './bill-summary-columns.tsx'
import { DEFAULT_BILL_SEARCH } from './const/bill-search.ts'
import type { BillRow } from './finance.types.ts'
import { RentalBillingPanel } from './rental-billing-panel.tsx'
import { toCents } from './utils/money.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function OrderBillsDrawer({
  order,
  onClose
}: {
  order: { type: 'rental' | 'service'; id: number; orderNo: string }
  onClose: () => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const canManageBills =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('bill.') && !permission.endsWith('.read'))
  const [open, setOpen] = useState(true)
  const [detailId, setDetailId] = useState<number>()
  const [creating, setCreating] = useState(false)
  const [detailAction, setDetailAction] = useState<'refund'>()
  const [operation, setOperation] = useState<BillOperation>()
  const [status, setStatus] = useState<'all' | 'draft' | 'issued' | 'voided'>('all')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })
  const orderFilter = order.type === 'rental' ? { rentalOrderId: order.id } : { serviceOrderId: order.id }
  const query = useQuery({
    ...trpc.bill.list.queryOptions({
      ...DEFAULT_BILL_SEARCH,
      ...pagination,
      direction: 'receivable',
      archived: 'all',
      ...orderFilter,
      ...(status === 'all' ? {} : { status: [status] })
    }),
    enabled: open,
    placeholderData: keepPreviousData
  })
  return (
    <Drawer
      open={open}
      size={DRAWER_SIZE}
      title={`订单收款账单 · ${order.orderNo}`}
      closable={{ placement: 'end' }}
      onClose={() => setOpen(false)}
      footer={
        <OrderBillsFooter
          order={order}
          canManage={canManageBills}
          loading={query.isFetching}
          failed={query.isError}
          total={query.data?.total}
          onClose={() => setOpen(false)}
          onCreate={() => setCreating(true)}
          onBill={setDetailId}
          onIssue={() =>
            setOperation({
              action: 'issue',
              target: {
                mode: 'search',
                search: {
                  ...DEFAULT_BILL_SEARCH,
                  ...orderFilter,
                  direction: 'receivable',
                  status: ['draft'],
                  archived: 'active'
                }
              }
            })
          }
        />
      }
      afterOpenChange={(visible) => {
        if (!visible) {
          onClose()
        }
      }}
    >
      {query.isError && (
        <Alert
          className="mb-4"
          showIcon
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      <p className="text-content-muted mb-4 text-sm">
        {order.type === 'rental' && '手动新建草稿用于临时费用；按规则生成用于周期费用和漏出的账期。'}
        草稿核费出账后才计入正式应收。
      </p>
      <div className="mb-4" role="group" aria-label="账单状态筛选">
        <Segmented<'all' | 'draft' | 'issued' | 'voided'>
          size="small"
          value={status}
          options={[
            { label: '全部', value: 'all' },
            { label: '待出账', value: 'draft' },
            { label: '已出账', value: 'issued' },
            { label: '已作废', value: 'voided' }
          ]}
          onChange={(next) => {
            setStatus(next)
            setPagination((previous) => ({ ...previous, current: 1 }))
          }}
        />
      </div>
      <ProTable<BillRow>
        cardProps={false}
        rowKey="id"
        size="small"
        search={false}
        columns={[
          ...createBillSummaryColumns({ compact: true, onBill: (bill) => setDetailId(bill.id) }),
          createBillNoteColumn({ compact: true }),
          {
            align: 'center',
            title: '操作',
            valueType: 'option',
            width: '12rem',
            fixed: 'right',
            render: (_, bill) => (
              <TableRowActions
                resource="bill"
                maxVisibleActions={1}
                actions={[
                  ...(canManageBills && bill.status === 'draft'
                    ? [
                        {
                          key: 'issue',
                          label: '核费出账',
                          onClick: () => setOperation({ action: 'issue', target: { mode: 'selected', ids: [bill.id] } })
                        }
                      ]
                    : []),
                  {
                    key: 'view',
                    label: '查看',
                    onClick: () => {
                      setDetailAction(undefined)
                      setDetailId(bill.id)
                    }
                  },
                  ...(canManageBills && bill.status === 'draft'
                    ? [
                        {
                          key: 'delete',
                          label: '删除草稿',
                          danger: true,
                          onClick: () =>
                            setOperation({ action: 'delete', target: { mode: 'selected', ids: [bill.id] } })
                        }
                      ]
                    : []),
                  ...(canManageBills &&
                  bill.status === 'issued' &&
                  toCents(bill.receivedAmount) > toCents(bill.refundedAmount)
                    ? [
                        {
                          key: 'refund',
                          label: '登记退款',
                          onClick: () => {
                            setDetailAction('refund')
                            setDetailId(bill.id)
                          }
                        }
                      ]
                    : [])
                ]}
              />
            )
          }
        ]}
        columnsState={{
          persistenceKey: 'orders-bills:table:v3',
          persistenceType: 'localStorage',
          defaultValue: { collectionStatus: { show: false }, note: { show: false } }
        }}
        toolBarRender={false}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        locale={{
          emptyText: query.isError ? (
            '账单暂时无法加载'
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前订单暂无收款账单" />
          )
        }}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          total: query.data?.total ?? 0,
          size: 'small',
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 张账单`,
          onChange: (current, pageSize) =>
            setPagination((previous) => ({
              current: pageSize === previous.pageSize ? current : 1,
              pageSize
            }))
        }}
      />
      {creating && (
        <BillEditor
          source={{ type: order.type, orderId: order.id }}
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false)
            setDetailAction(undefined)
            setDetailId(id)
          }}
        />
      )}
      {detailId && (
        <BillDetail
          key={detailId}
          billId={detailId}
          initialAction={detailAction}
          canManage={canManageBills}
          onClose={() => {
            setDetailId(undefined)
            setDetailAction(undefined)
          }}
        />
      )}
      {operation && (
        <BillActionDialog
          operation={operation}
          onClose={() => setOperation(undefined)}
          onGroup={() => {}}
          onSaved={async () => {
            await refreshMoneyQueries(cache, trpc)
          }}
        />
      )}
    </Drawer>
  )
}

function OrderBillsFooter({
  order,
  canManage,
  loading,
  failed,
  total,
  onClose,
  onCreate,
  onBill,
  onIssue
}: {
  order: { type: 'rental' | 'service'; id: number }
  canManage: boolean
  loading: boolean
  failed: boolean
  total: number | undefined
  onClose: () => void
  onCreate: () => void
  onBill: (id: number) => void
  onIssue: () => void
}) {
  return (
    <div className="flex min-h-10 flex-wrap items-center justify-between gap-3" role="group" aria-label="订单账单操作">
      <div className="flex flex-wrap items-center gap-2">
        {canManage && order.type === 'rental' && <RentalBillingPanel rentalOrderId={order.id} onBill={onBill} />}
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <Button size="medium" onClick={onClose}>
          关闭
        </Button>
        {canManage && (
          <>
            <Authorized permissions={['bill.create']}>
              <Button size="medium" onClick={onCreate}>
                手动新建草稿
              </Button>
            </Authorized>
            <Button size="medium" type="primary" disabled={loading || failed || !total} onClick={onIssue}>
              批量核费出账
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
